import React, { useEffect, useRef, useState, useCallback } from "react";

/* ============================================================
   Reveal - scroll-triggered entrance.
   Everything on the site enters the same way: a short rise and
   a long settle. Consistency is what makes it read as calm.
   ============================================================ */
export const Reveal = ({
  children,
  delay = 0,
  as: Tag = "div",
  className = "",
  ...rest
}) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          io.unobserve(el);
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${seen ? "is-in" : ""} ${className}`}
      style={{ "--reveal-delay": `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/* Clip-mask reveal for display headlines - the line slides up
   out of its own overflow box. */
export const MaskLine = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          io.unobserve(el);
        }
      },
      { threshold: 0.2 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <span
      ref={ref}
      className={`reveal-mask ${seen ? "is-in" : ""} ${className}`}
      style={{ "--reveal-delay": `${delay}ms` }}
    >
      <span>{children}</span>
    </span>
  );
};

/* ============================================================
   SectionMark - the drafting header.
   "STACK ----------------------------------- 29 ITEMS"
   ============================================================ */
export const SectionMark = ({ title, meta }) => (
  <Reveal className="flex items-baseline gap-4 md:gap-6 pb-6">
    <span className="label text-ink shrink-0">{title}</span>
    <span className="flex-1 h-px bg-[var(--rule)] translate-y-[-3px]" />
    {meta && <span className="label shrink-0 hidden sm:block">{meta}</span>}
  </Reveal>
);

/* ============================================================
   GridLines - the exposed structural columns.
   Fixed behind the page so content scrolls against a static
   frame, the way a building's columns stay put as you walk.
   ============================================================ */
export const GridLines = ({ count = 5 }) => (
  <div className="gridlines" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <span
        key={i}
        style={{ left: `${((i + 1) / (count + 1)) * 100}%` }}
        className={i === 0 || i === count - 1 ? "hidden md:block" : ""}
      />
    ))}
  </div>
);

/* Four registration crosshairs, inset from a panel's corners. */
export const RegMarks = ({ inset = 12 }) => (
  <>
    <span className="regmark" style={{ top: inset, left: inset }} />
    <span className="regmark" style={{ top: inset, right: inset }} />
    <span className="regmark" style={{ bottom: inset, left: inset }} />
    <span className="regmark" style={{ bottom: inset, right: inset }} />
  </>
);

/* ============================================================
   useSpecular - feeds cursor position into the glass highlight
   as --mx / --my so the sheen tracks the pointer.
   ============================================================ */
export const useSpecular = () => {
  const ref = useRef(null);

  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  }, []);

  return { ref, onMouseMove: onMove };
};

/* A liquid-glass pane that catches the light where the cursor is. */
export const Glass = ({ as: Tag = "div", className = "", children, ...rest }) => {
  const { ref, onMouseMove } = useSpecular();

  return (
    <Tag
      ref={ref}
      onMouseMove={onMouseMove}
      className={`glass glass-live ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/* ============================================================
   Shell - the single content measure used site-wide.
   ============================================================ */
export const Shell = ({ className = "", children }) => (
  <div className={`mx-auto w-full max-w-shell px-6 md:px-10 ${className}`}>
    {children}
  </div>
);
